import {
  Box,
  Flex,
  Grid,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  Tooltip,
  useTheme,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  HiOutlineClipboardCopy,
  HiOutlineSearch,
  HiOutlineX,
} from "react-icons/hi";
import { HiArrowDownTray } from "react-icons/hi2";
import { SingleValue } from "react-select";

import { Button, Card, LoadingIndicator, useToast } from "../../../components";
import { Interval } from "../../../components/SelectDateRange/SelectDateRange";
import { SelectOptionalDateRangeState } from "../../../components/SelectOptionalDateRange/SelectOptionalDateRange";
import useDebounce from "../../../hooks/useDebounce";
import useIntersectionObserver from "../../../hooks/useIntersectionObserver";
import {
  useDateSearchParam,
  useSearchParam,
} from "../../../hooks/useSearchParam";
import {
  CaptureStatusEnum,
  InterviewImportDecisionReason,
  ScheduledInterviewListItemV2,
  useScheduledInterviewsCsvLazyQuery,
  useScheduledInterviewsLazyQuery,
} from "../../graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../graphql/hooks/useFeatureFlag";
import DefaultLayout from "../../layouts/DefaultLayout";
import InterviewsList from "./InterviewsList";
import SearchFilters from "./SearchFilters";
import {
  captureStatusOptions,
  createFilteredCaptureStatusOptions,
  createFilteredDepartmentOptions,
  createFilteredPositionOptions,
  createFilteredRecordingReasonOptions,
  createFilteredRecordingStatusOptions,
  findOptionInGroupedOptions,
  getScheduledInterviewsAsTSV,
  loadScheduledInterviews,
  recordingReasonOptions,
  recordingStatusOptions,
  SelectedOption,
} from "./utils";

const LIMIT = 50;
const oneDayInMs = 24 * 60 * 60 * 1000;
const now = new Date();
const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
const sixtyDaysFromNow = new Date(startOfToday.getTime() + 60 * oneDayInMs);

const ScheduledInterviewsPage: React.FC = () => {
  const theme = useTheme();
  const { pagePadding } = theme.sizes;
  const toast = useToast();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const inView = useIntersectionObserver(sentinelRef);

  const isCsvDownloadEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "scheduled_interviews_v2_csv"
  );

  // URL parameters & search state
  const [query, setQuery] = useSearchParam("query");
  const [start, setStart] = useDateSearchParam("start");
  const [end, setEnd] = useDateSearchParam("end");
  const [captureStatusParam, setCaptureStatusParam] =
    useSearchParam("captureStatus");
  const [recordingReasonParam, setRecordingReasonParam] =
    useSearchParam("recordingReason");
  const [recordingStatusParam, setRecordingStatusParam] =
    useSearchParam("recordingDisabled");
  const [positionParam, setPositionParam] = useSearchParam("position");
  const [departmentParam, setDepartmentParam] = useSearchParam("department");

  const [searchInputValue, setSearchInputValue] = useState<string>(query || "");
  const debouncedSearch = useDebounce(searchInputValue, 350);

  // Filter options & state
  const [captureStatus, setCaptureStatus] = useState<SelectedOption | null>(
    captureStatusOptions.find((o) => o.value === captureStatusParam) || null
  );
  const [recordingReason, setRecordingReason] = useState<SelectedOption | null>(
    findOptionInGroupedOptions(recordingReasonOptions, recordingReasonParam) ||
      null
  );
  const [selectedRecordingStatus, setSelectedRecordingStatus] = useState<
    SingleValue<SelectedOption>
  >(
    recordingStatusOptions.find((o) => o.value === recordingStatusParam) || null
  );
  const [selectedPosition, setSelectedPosition] =
    useState<SingleValue<SelectedOption>>(null);
  const [selectedDepartment, setSelectedDepartment] =
    useState<SingleValue<SelectedOption>>(null);

  // Available filter values from backend
  const [availableFilterValues, setAvailableFilterValues] = useState<{
    captureStatuses?: string[];
    importDecisions?: boolean[];
    importDecisionReasons?: string[];
    positions?: Array<{ id: string; value: string }>;
    clients?: Array<{ id: string; value: string }>;
  }>({});

  // Date range state
  const [dateRange, setDateRange] = useState<SelectOptionalDateRangeState>({
    start: start ? new Date(start) : startOfToday,
    end: end ? new Date(end) : startOfToday,
    selected: "3",
    interval: Interval.Week,
  });

  // Data & pagination state
  const [scheduledInterviews, setScheduledInterviews] = useState<
    ScheduledInterviewListItemV2[]
  >([]);
  const [paginationState, setPaginationState] = useState({
    nextPageStartTimestamp: undefined as string | undefined,
    nextPageStartId: undefined as string | undefined,
    hasNextPage: false,
    totalRows: undefined as number | undefined,
  });

  const [getScheduledInterviews, { loading, error }] =
    useScheduledInterviewsLazyQuery();

  const [fetchCsv, { loading: csvLoading }] =
    useScheduledInterviewsCsvLazyQuery({
      fetchPolicy: "network-only",
      onCompleted(data) {
        if (data?.scheduledInterviewsCsv?.url) {
          const url = data?.scheduledInterviewsCsv?.url;
          const downloadLink = document.createElement("a");
          downloadLink.style.display = "none";
          downloadLink.href = url;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        }
      },
    });

  // Computed filter options based on available filter values
  const filteredCaptureStatusOptions = useMemo(
    () =>
      createFilteredCaptureStatusOptions(availableFilterValues.captureStatuses),
    [availableFilterValues.captureStatuses]
  );

  const filteredRecordingStatusOptions = useMemo(
    () =>
      createFilteredRecordingStatusOptions(
        availableFilterValues.importDecisions
      ),
    [availableFilterValues.importDecisions]
  );

  const filteredRecordingReasonOptions = useMemo(
    () =>
      createFilteredRecordingReasonOptions(
        availableFilterValues.importDecisionReasons
      ),
    [availableFilterValues.importDecisionReasons]
  );

  const filteredPositionOptions = useMemo(
    () => createFilteredPositionOptions(availableFilterValues.positions),
    [availableFilterValues.positions]
  );

  const filteredDepartmentOptions = useMemo(
    () => createFilteredDepartmentOptions(availableFilterValues.clients),
    [availableFilterValues.clients]
  );

  // Helper functions
  const clearPaginationState = (): void => {
    setPaginationState({
      nextPageStartTimestamp: undefined,
      nextPageStartId: undefined,
      hasNextPage: false,
      totalRows: undefined,
    });
  };

  const handleDataLoaded = (data: any, append: boolean): void => {
    setScheduledInterviews((prev) =>
      append
        ? [...prev, ...(data?.scheduledInterviews?.results ?? [])]
        : data?.scheduledInterviews?.results ?? []
    );
    setPaginationState({
      hasNextPage: data?.scheduledInterviews.pageInfo?.hasNextPage ?? false,
      nextPageStartTimestamp:
        data?.scheduledInterviews.pageInfo?.nextPageStartTimestamp,
      nextPageStartId:
        data?.scheduledInterviews.pageInfo?.nextPageStartId ?? undefined,
      totalRows: !data?.scheduledInterviews.pageInfo?.hasPreviousPage
        ? data?.scheduledInterviews.pageInfo?.totalRows ?? undefined
        : paginationState.totalRows,
    });

    // Update available filter values from backend response (only on initial load)
    if (!append && data?.scheduledInterviews?.filterValues) {
      setAvailableFilterValues({
        captureStatuses: data.scheduledInterviews.filterValues.captureStatuses,
        importDecisions: data.scheduledInterviews.filterValues.importDecisions,
        importDecisionReasons:
          data.scheduledInterviews.filterValues.importDecisionReasons,
        positions: data.scheduledInterviews.filterValues.positions,
        clients: data.scheduledInterviews.filterValues.clients,
      });
    }
  };

  // Event handlers
  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSearchInputValue(event.target.value);
    setQuery(event.target.value);
  };

  const handleClearSearch = (): void => {
    setSearchInputValue("");
    setQuery("");
  };

  const handleSelectDateRange = (
    newDateRange: SelectOptionalDateRangeState
  ): void => {
    setDateRange(newDateRange);
  };

  const handleCaptureStatusChange = (
    selectedOption: SingleValue<SelectedOption>
  ): void => {
    setCaptureStatus(selectedOption ?? null);
    setCaptureStatusParam(selectedOption?.value);
  };

  const handleRecordingReasonChange = (
    selectedOption: SingleValue<SelectedOption>
  ): void => {
    setRecordingReason(selectedOption ?? null);
    setRecordingReasonParam(selectedOption?.value);
  };

  const handleRecordingDisabledChange = (
    selectedOption: SingleValue<SelectedOption>
  ): void => {
    setSelectedRecordingStatus(selectedOption);
    setRecordingStatusParam(selectedOption?.value || "");
  };

  const handlePositionChange = (
    selectedOption: SingleValue<SelectedOption>
  ): void => {
    setSelectedPosition(selectedOption);
    setPositionParam(selectedOption?.value || "");
  };

  const handleDepartmentChange = (
    selectedOption: SingleValue<SelectedOption>
  ): void => {
    setSelectedDepartment(selectedOption);
    setDepartmentParam(selectedOption?.value || "");
  };

  const onLoadMore = (): void => {
    loadScheduledInterviews({
      fromTime: paginationState.nextPageStartTimestamp,
      append: true,
      dateRange,
      limit: LIMIT,
      debouncedSearch,
      captureStatus,
      recordingReason,
      selectedRecordingStatus,
      selectedPosition,
      selectedDepartment,
      nextPageStartId: paginationState.nextPageStartId,
      nextPageStartTimestamp: paginationState.nextPageStartTimestamp,
      getScheduledInterviews,
      onCompleted: (data) => handleDataLoaded(data, true),
    });
  };

  // Effects - URL sync
  useEffect(() => {
    setCaptureStatus(
      captureStatusOptions.find((o) => o.value === captureStatusParam) || null
    );
  }, [captureStatusParam]);

  useEffect(() => {
    setRecordingReason(
      findOptionInGroupedOptions(
        recordingReasonOptions,
        recordingReasonParam
      ) ?? null
    );
  }, [recordingReasonParam, recordingReasonOptions]);

  useEffect(() => {
    setSelectedRecordingStatus(
      recordingStatusOptions.find((o) => o.value === recordingStatusParam) ??
        null
    );
  }, [recordingStatusParam]);

  useEffect(() => {
    const { start, end } = dateRange;
    setStart(start);
    setEnd(end);
  }, [dateRange]);

  useEffect(() => {
    setSelectedPosition(
      filteredPositionOptions.find((o) => o.value === positionParam) ?? null
    );
  }, [positionParam, filteredPositionOptions]);

  useEffect(() => {
    setSelectedDepartment(
      filteredDepartmentOptions.find((o) => o.value === departmentParam) ?? null
    );
  }, [departmentParam, filteredDepartmentOptions]);

  // Effects - data loading
  useEffect(() => {
    clearPaginationState();
    loadScheduledInterviews({
      fromTime: dateRange.start?.toISOString(),
      append: false,
      dateRange,
      debouncedSearch,
      captureStatus,
      recordingReason,
      selectedRecordingStatus,
      selectedPosition,
      selectedDepartment,
      nextPageStartId: paginationState.nextPageStartId,
      nextPageStartTimestamp: paginationState.nextPageStartTimestamp,
      getScheduledInterviews,
      onCompleted: (data) => handleDataLoaded(data, false),
    });
  }, [
    dateRange.start,
    dateRange.end,
    debouncedSearch,
    captureStatus?.value,
    recordingReason?.value,
    selectedRecordingStatus?.value,
    selectedPosition?.value,
    selectedDepartment?.value,
  ]);

  useEffect(() => {
    if (
      inView &&
      paginationState.hasNextPage &&
      scheduledInterviews.length >= LIMIT
    ) {
      onLoadMore();
    }
  }, [inView, paginationState.hasNextPage]);

  // Render
  if (error) {
    return (
      <DefaultLayout>
        <Box textAlign="center" p={5}>
          <Heading size="md">Error loading scheduled interviews</Heading>
          <Box mt={4}>{error.message}</Box>
        </Box>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <Grid
        p={pagePadding}
        gap="5"
        overflowX="auto"
        templateColumns="300px minmax(auto, 1fr)"
      >
        {/* Left side - Filters */}
        <Box>
          <SearchFilters
            captureStatus={captureStatus}
            recordingReason={recordingReason}
            recordingStatus={selectedRecordingStatus}
            position={selectedPosition}
            department={selectedDepartment}
            dateRange={dateRange}
            captureStatusOptions={filteredCaptureStatusOptions}
            recordingReasonOptions={filteredRecordingReasonOptions}
            recordingStatusOptions={filteredRecordingStatusOptions}
            positionOptions={filteredPositionOptions}
            departmentOptions={filteredDepartmentOptions}
            onCaptureStatusChange={handleCaptureStatusChange}
            onRecordingReasonChange={handleRecordingReasonChange}
            onRecordingStatusChange={handleRecordingDisabledChange}
            onPositionChange={handlePositionChange}
            onDepartmentChange={handleDepartmentChange}
            onDateRangeChange={handleSelectDateRange}
            onClearAllFilters={() => {
              setCaptureStatusParam("");
              setRecordingReasonParam("");
              setRecordingStatusParam("");
              setPositionParam("");
              setDepartmentParam("");
            }}
            loading={loading}
            maxDate={sixtyDaysFromNow}
          />
        </Box>

        {/* Right side - Search and Results */}
        <Box>
          <Card mb={4}>
            <Box p={4}>
              <Flex gap={3}>
                <InputGroup flex={1}>
                  <InputLeftElement h="8" px="0" color="placeholder">
                    <Icon as={HiOutlineSearch} boxSize="4" />
                  </InputLeftElement>
                  <Input
                    value={searchInputValue}
                    placeholder="Search scheduled interviews"
                    onChange={handleSearchChange}
                    h="8"
                    pr="12"
                    fontSize="sm"
                  />
                  <InputRightElement h="8" w="12">
                    {loading ? (
                      <LoadingIndicator boxSize="4" delay={0} size="sm" />
                    ) : null}
                    {searchInputValue && (
                      <Icon
                        as={HiOutlineX}
                        boxSize="4"
                        ml="auto"
                        mr="2"
                        onClick={handleClearSearch}
                        cursor="pointer"
                      />
                    )}
                  </InputRightElement>
                </InputGroup>

                {scheduledInterviews &&
                  scheduledInterviews.length > 0 &&
                  !loading &&
                  !error && (
                    <Text fontSize="sm" color="gray.600" mt={2} ml={1}>
                      Showing {scheduledInterviews.length} of{" "}
                      {paginationState.totalRows} results
                    </Text>
                  )}

                {isCsvDownloadEnabled ? (
                  <Tooltip label="Maximum 10,000 results can be downloaded at once. Contact your CSM for a larger export.">
                    <Button
                      aria-label="Download full CSV results"
                      leftIcon={<Icon as={HiArrowDownTray} boxSize="5" />}
                      isLoading={csvLoading || loading}
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if ((scheduledInterviews?.length ?? 0) > 1000) {
                          toast({
                            title: "Generating CSV...",
                            description: "This may take a minute.",
                            status: "success",
                          });
                        }
                        const toTime = dateRange.end
                          ? new Date(dateRange.end.getTime() + oneDayInMs)
                          : undefined;
                        fetchCsv({
                          variables: {
                            fromTime: dateRange.start,
                            toTime,
                            query: debouncedSearch || undefined,
                            captureStatus: captureStatus?.value as
                              | CaptureStatusEnum
                              | undefined,
                            positionId: selectedPosition?.value || undefined,
                            departmentId:
                              selectedDepartment?.value || undefined,
                            importDecision:
                              selectedRecordingStatus?.value === "true"
                                ? true
                                : selectedRecordingStatus?.value === "false"
                                ? false
                                : undefined,
                            importDecisionReason: recordingReason?.value as
                              | InterviewImportDecisionReason
                              | undefined,
                          },
                        });
                      }}
                      disabled={(scheduledInterviews?.length ?? 0) === 0}
                    >
                      Download CSV
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(
                          getScheduledInterviewsAsTSV(scheduledInterviews)
                        );
                        toast({
                          title: "Copied to clipboard",
                          status: "success",
                          duration: 2000,
                        });
                      } catch (err) {
                        toast({
                          title: "Failed to copy",
                          description: "Please try again",
                          status: "error",
                        });
                      }
                    }}
                    size="sm"
                    px={4}
                    leftIcon={<HiOutlineClipboardCopy />}
                    disabled={(scheduledInterviews?.length ?? 0) === 0}
                  >
                    Copy results
                  </Button>
                )}
              </Flex>
            </Box>
            <InterviewsList
              loading={loading}
              scheduledInterviews={scheduledInterviews}
            />
            <Box width="100%" ref={sentinelRef} />
            {loading && <LoadingIndicator pt={2} pb={2} delay={0} />}
            {!loading &&
              !paginationState.hasNextPage &&
              paginationState?.totalRows !== undefined &&
              paginationState.totalRows > LIMIT && (
                <Box
                  width="100%"
                  color="gray.500"
                  fontSize="sm"
                  py={6}
                  textAlign="center"
                >
                  No more results.
                </Box>
              )}
          </Card>
        </Box>
      </Grid>
    </DefaultLayout>
  );
};

export default ScheduledInterviewsPage;
